import { Avatar, Box, FileInput } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { ModalEditUserAvatar } from "./components/ModalEditUserAvatar";
import {
  confirmAvatarUpdate,
  createCroppedAreaHandler,
  createCroppedImage,
  getCreateAvatarImage,
  resetAvatarSettings,
} from "./functions";

import { useProfile } from "@/services/profile/get-profile";
import { useUpdateAvatar } from "@/services/profile/upload-user-image";
import { getUserName } from "@/utils/getUserName";

import type { User } from "@/types/user";
import type { AvatarProps } from "@mantine/core";
import type { FC } from "react";
import type { Area } from "react-easy-crop";

export type UserAvatarProps = {
  enableUpload: boolean;
  size?: AvatarProps["size"];
  user?: User;
  variant?: AvatarProps["variant"];
};

const createImage = getCreateAvatarImage();
const getCroppedImg = createCroppedImage(createImage);

export const UserAvatar: FC<UserAvatarProps> = ({
  enableUpload,
  size,
  variant = "light",
  user,
}) => {
  const { t } = useTranslation();
  const name = getUserName(user);

  const [avatarUrl, setAvatarUrl] = useState<{ url: string; name: string } | null>(
    user?.additional_info?.avatarImage?.url && user?.additional_info?.avatarImage?.name
      ? { url: user.additional_info.avatarImage.url, name: user.additional_info.avatarImage.name }
      : null
  );

  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string>("");
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [opened, { open, close }] = useDisclosure(false);

  // Handlers
  const updateAvatarMutation = useUpdateAvatar({
    mutationConfig: {
      onSuccess: (data) => {
        if (data.additional_info?.avatarImage) {
          setAvatarUrl({
            url: data.additional_info.avatarImage.url ?? "",
            name: data.additional_info.avatarImage.name ?? "",
          });
        }
      },
    },
  });

  const onCropComplete = createCroppedAreaHandler(setCroppedAreaPixels);

  const handleFileChange = (file: File | null) => {
    if (!file) return;

    setFileName(file.name);
    setImageSrc(URL.createObjectURL(file));
    open();
  };

  const handleCancel = resetAvatarSettings(close, setImageSrc, setCrop, setZoom);

  const upload = confirmAvatarUpdate(
    imageSrc,
    croppedAreaPixels,
    fileName,
    setAvatarUrl,
    updateAvatarMutation,
    t,
    close,
    setImageSrc,
    setCrop,
    setZoom,
    getCroppedImg
  );

  const profileQuery = useProfile();
  useEffect(() => {
    if (profileQuery.data?.additional_info?.avatarImage) {
      setAvatarUrl({
        url: profileQuery.data.additional_info.avatarImage.url ?? "",
        name: profileQuery.data.additional_info.avatarImage.name ?? "",
      });
    } else {
      setAvatarUrl(null);
    }
  }, [profileQuery.data]);

  return (
    <>
      <Box pos="relative" display="inline-block">
        <Avatar
          color="primary"
          size={size}
          variant={variant}
          name={name}
          src={avatarUrl?.url}
          alt={name ? name : ""}
        />

        {/* Upload Button: if enabled, allows users to click anywhere on the avatar to upload a new image */}
        {enableUpload && (
          <FileInput
            onChange={handleFileChange}
            accept="image/png,image/jpeg,image/gif"
            aria-label={t("profile.change_profile_image")}
            styles={{
              root: {
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
              },
              wrapper: {
                width: "100%",
                height: "100%",
              },
              input: {
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                opacity: 0,
                cursor: "pointer",
                borderRadius: "50%",
              },
            }}
          />
        )}
      </Box>

      {/* Upload Modal: This is used for uploading and cropping the new user image */}
      <ModalEditUserAvatar
        opened={opened}
        handleCancel={handleCancel}
        onCropComplete={onCropComplete}
        crop={crop}
        setCrop={setCrop}
        zoom={zoom}
        setZoom={setZoom}
        imageSrc={imageSrc}
        upload={upload}
        updateAvatarMutation={updateAvatarMutation}
      />
    </>
  );
};
